import { Checkbox, Popover, Segmented } from 'antd';
import { ROTATION_PAST_YEARS_HELP } from 'constants/messageConstants';
import {
  ROTATION_PAST_YEARS_LABEL,
  ROTATION_SHOW_AREA_LABEL,
  ROTATION_SHOW_LABEL,
  ROTATION_SHOW_PRODUCTION_LABEL,
  ROTATION_SHOW_SOWING_LABEL,
  ROTATION_SHOW_VARIETY_LABEL,
  ROTATION_VIEW_OPTIONS_LABEL,
  SUMMARY_DATA_BASIS_ACTUAL,
  SUMMARY_DATA_BASIS_LABELS,
  SUMMARY_DATA_BASIS_PLAN,
} from 'constants/uiConstants';
import { InputTaskCategory } from 'interfaces/IInputTask';
import React from 'react';
import { LuSlidersHorizontal } from 'react-icons/lu';
import type { RotationCellDetails } from '../../../../../redux/slices/InsightsSlice';

const DETAIL_OPTIONS: { key: keyof RotationCellDetails; label: string }[] = [
  { key: 'area', label: ROTATION_SHOW_AREA_LABEL },
  { key: 'variety', label: ROTATION_SHOW_VARIETY_LABEL },
  { key: 'production', label: ROTATION_SHOW_PRODUCTION_LABEL },
  { key: 'sowing', label: ROTATION_SHOW_SOWING_LABEL },
];

const PAST_CATEGORY_OPTIONS = [
  {
    label: SUMMARY_DATA_BASIS_LABELS[SUMMARY_DATA_BASIS_ACTUAL],
    value: InputTaskCategory.Actual,
  },
  {
    label: SUMMARY_DATA_BASIS_LABELS[SUMMARY_DATA_BASIS_PLAN],
    value: InputTaskCategory.Plan,
  },
];

/** Same ghost pill as the Analysis lens's Columns control. */
export const PILL_BUTTON_CLASS =
  'inline-flex cursor-pointer items-center gap-1.5 rounded-full border border-transparent bg-transparent px-2.5 py-1 text-sm font-semibold text-charcoal transition-colors hover:border-charcoal hover:bg-rowHover';

interface RotationViewOptionsProps {
  details: RotationCellDetails;
  onDetailsChange: (details: RotationCellDetails) => void;
  planningSeason: number;
  pastCategory: InputTaskCategory;
  onPastCategoryChange: (category: InputTaskCategory) => void;
}

/**
 * The Rotation planner's View options (OP-2501): what each cell shows, and
 * whether years before the planning year show Plan or Actual yield and
 * sowing. Kept behind one button so the grid has the room.
 */
const RotationViewOptions: React.FC<RotationViewOptionsProps> = ({
  details,
  onDetailsChange,
  planningSeason,
  pastCategory,
  onPastCategoryChange,
}) => {
  const content = (
    <div className="flex w-64 flex-col gap-3 p-1">
      <div className="flex flex-col gap-0.5">
        <p className="mb-1 text-sm font-semibold text-gray-dark">
          {ROTATION_SHOW_LABEL}
        </p>
        {DETAIL_OPTIONS.map(({ key, label }) => (
          <label
            key={key}
            className="flex cursor-pointer items-center rounded px-2 py-1 hover:bg-rowHover"
          >
            <Checkbox
              checked={details[key]}
              onChange={(event) =>
                onDetailsChange({ ...details, [key]: event.target.checked })
              }
            >
              {label}
            </Checkbox>
          </label>
        ))}
      </div>
      <div className="flex flex-col gap-1 border-t border-gray-bright pt-2">
        <p className="text-sm font-semibold text-gray-dark">
          {ROTATION_PAST_YEARS_LABEL(planningSeason)}
        </p>
        <Segmented<InputTaskCategory>
          aria-label={ROTATION_PAST_YEARS_LABEL(planningSeason)}
          className="w-fit"
          value={pastCategory}
          options={PAST_CATEGORY_OPTIONS}
          onChange={onPastCategoryChange}
        />
        <p className="text-sm text-gray-dark">{ROTATION_PAST_YEARS_HELP}</p>
      </div>
    </div>
  );

  return (
    <Popover trigger="click" placement="bottomRight" content={content}>
      <button type="button" className={PILL_BUTTON_CLASS}>
        <LuSlidersHorizontal size={14} aria-hidden />
        {ROTATION_VIEW_OPTIONS_LABEL}
      </button>
    </Popover>
  );
};

export default RotationViewOptions;
